import { computed, inject, Injectable, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators, type AbstractControl, type ValidationErrors } from '@angular/forms';
import { Dialog } from '@angular/cdk/dialog';
import { EntityEditFacade, FormattingService, type ProfileDangerAction, type ProfileQuickAction, type ProfileStat } from '@zag/ui';
import { type WebhookEndpointResponse, DEFAULT_CIRCUIT_BREAKER_THRESHOLD } from '@zag/notifications/common';
import { WebhookEndpointService } from '../services/webhook-endpoint.service';
import { WebhookCrudStrategy } from './webhook-crud.strategy';
import {
  WebhookSecretDialogComponent,
  type WebhookSecretDialogData,
  type WebhookSecretDialogResult,
} from './webhook-secret-dialog.component';

// ── Validators ──────────────────────────────────────────────────────────────────

/**
 * Mirrors the backend SSRF blocklist: rejects localhost and common private hostnames.
 * Full IP-range checks happen server-side; this catches the most common mistakes early.
 */
function webhookUrlValidator(control: AbstractControl): ValidationErrors | null {
  const value: string = control.value ?? '';
  if (!value) return null;

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }

  const hostname = parsed.hostname.toLowerCase();

  if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
    return { urlNotAllowed: true };
  }

  if (
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.startsWith('10.') ||
    hostname.startsWith('192.168.') ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(hostname)
  ) {
    return { urlNotAllowed: true };
  }

  return null;
}

// ── Types ───────────────────────────────────────────────────────────────────────

export const EVENT_TYPES = [
  'shift.assigned',
  'shift.unassigned',
  'shift.updated',
  'plan.published',
  'plan.updated',
  'availability.requested',
  'availability.approved',
  'availability.rejected',
  'shift_time_override_by_employee',
  'shift_override_reset_by_planner',
];

export interface WebhookForm extends Record<string, unknown> {
  name: string;
  url: string;
  isActive: boolean;
  customHeaders: { key: string; value: string }[];
}

// ── Facade ──────────────────────────────────────────────────────────────────────

@Injectable()
export class WebhookEditFacade extends EntityEditFacade<WebhookEndpointResponse, WebhookForm> {
  private readonly webhookService = inject(WebhookEndpointService);
  private readonly dialog = inject(Dialog);
  private readonly formattingService = inject(FormattingService);

  readonly config = {
    resourceName: 'webhooks',
    routePath: '/settings/webhooks',
    i18nPrefix: 'webhooks',
    defaultIcon: 'webhook',
    supportsAppearance: false,
    supportsAuditLog: false,
  };

  readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(1)] }),
    url: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^https?:\/\/.+/), webhookUrlValidator],
    }),
    isActive: new FormControl(true, { nonNullable: true }),
    customHeaders: new FormArray<FormGroup<{ key: FormControl<string>; value: FormControl<string> }>>([]),
  });

  // ── Webhook-specific state ──────────────────────────────────────────────────

  /** Event types stored outside the form (chip-toggle UI) */
  readonly selectedEventTypes = signal<Set<string>>(new Set());

  /** Whether a test delivery is in progress */
  readonly isTesting = signal(false);

  /** Whether the circuit breaker has been triggered */
  readonly isCircuitOpen = computed(() => {
    const ep = this.entity();
    return ep ? ep.consecutiveFailures >= DEFAULT_CIRCUIT_BREAKER_THRESHOLD : false;
  });

  /** Count of non-placeholder (non-empty key) headers for the accordion badge */
  readonly customHeaderCount = computed(() => {
    this.formValues();
    return this.form.controls.customHeaders.controls.filter(g => g.controls['key'].value).length;
  });

  // ── Computed display signals ────────────────────────────────────────────────

  readonly webhookName = computed(() => {
    this.currentLang();
    const val = this.formValues() as Partial<WebhookForm>;
    return val?.name || (this.isNew() ? this.translate.instant('webhooks.new') : '\u2014');
  });

  readonly webhookSubtitle = computed(() => {
    this.currentLang();
    const ep = this.entity();
    if (!ep) return this.translate.instant('webhooks.entityType');
    return ep.isActive ? this.translate.instant('webhooks.status.active') : this.translate.instant('webhooks.status.inactive');
  });

  constructor() {
    super();
    this.init();
  }

  // ── EntityEditFacade abstract implementations ───────────────────────────────

  protected getCrudStrategy() {
    return new WebhookCrudStrategy(this.webhookService);
  }

  mapEntityToForm(endpoint: WebhookEndpointResponse): Partial<WebhookForm> {
    return {
      name: endpoint.name,
      url: endpoint.url,
      isActive: endpoint.isActive,
    };
  }

  mapFormToPayload(): unknown {
    const { name, url, isActive, customHeaders } = this.form.getRawValue();
    const customHeadersObj =
      customHeaders.length > 0 ? Object.fromEntries(customHeaders.filter(h => h.key).map(h => [h.key, h.value])) : null;

    return {
      name,
      url,
      is_active: isActive,
      event_types: Array.from(this.selectedEventTypes()),
      custom_headers: customHeadersObj,
    };
  }

  getStats(endpoint: WebhookEndpointResponse | null): ProfileStat[] {
    if (!endpoint) return [];
    return [
      {
        label: this.translate.instant('webhooks.fields.eventTypes'),
        value: String(endpoint.eventTypes.length),
        icon: 'event',
      },
      {
        label: this.translate.instant('webhooks.stats.consecutiveFailures'),
        value: String(endpoint.consecutiveFailures),
        icon: 'error_outline',
      },
      {
        label: this.translate.instant('webhooks.stats.created'),
        value: this.formattingService.formatDate(endpoint.createdAt),
        icon: 'calendar_today',
      },
    ];
  }

  getFormResetValues(): Partial<WebhookForm> {
    return { name: '', url: '', isActive: true };
  }

  // ── Lifecycle hooks ─────────────────────────────────────────────────────────

  protected override afterFormPopulated(endpoint: WebhookEndpointResponse): void {
    // Populate custom headers FormArray
    this.form.controls.customHeaders.clear();
    if (endpoint.customHeaders) {
      for (const [key, value] of Object.entries(endpoint.customHeaders)) {
        this.form.controls.customHeaders.push(
          new FormGroup({
            key: new FormControl(key, { nonNullable: true }),
            value: new FormControl(value, { nonNullable: true }),
          }),
          { emitEvent: false }
        );
      }
    }
    this.ensurePlaceholderRow();
    this.selectedEventTypes.set(new Set(endpoint.eventTypes));
    this.form.markAsPristine();
  }

  protected override async afterSaveSuccess(saved: WebhookEndpointResponse): Promise<void> {
    if ('secret' in saved && typeof (saved as unknown as Record<string, unknown>)['secret'] === 'string') {
      await this.showSecretDialog(saved as unknown as WebhookEndpointResponse & { secret: string });
    }

    this.webhookService.listResource.reload();
  }

  protected override afterDeleteSuccess(): void {
    this.webhookService.listResource.reload();
  }

  // ── Overrides ───────────────────────────────────────────────────────────────

  override onSave(): void {
    if (this.selectedEventTypes().size === 0) {
      this.form.markAllAsTouched();
      return;
    }
    super.onSave();
  }

  protected override getQuickActions(): ProfileQuickAction[] {
    if (this.isNew()) return [];
    return [
      {
        id: 'test',
        label: this.translate.instant('webhooks.actions.test'),
        icon: 'send',
        disabled: this.isTesting(),
      },
    ];
  }

  protected override getDangerActions(): ProfileDangerAction[] {
    if (this.isNew()) return [];
    return [
      {
        id: 'delete',
        label: this.translate.instant('webhooks.actions.delete'),
        icon: 'delete',
      },
    ];
  }

  // ── Webhook-specific methods ────────────────────────────────────────────────

  toggleEventType(eventType: string): void {
    const current = new Set(this.selectedEventTypes());
    if (current.has(eventType)) {
      current.delete(eventType);
    } else {
      current.add(eventType);
    }
    this.selectedEventTypes.set(current);
    this.form.markAsDirty();
  }

  isEventTypeSelected(eventType: string): boolean {
    return this.selectedEventTypes().has(eventType);
  }

  get customHeaderControls() {
    return this.form.controls.customHeaders.controls;
  }

  /**
   * Ensures there is always exactly one empty placeholder row at the end of the
   * custom headers list. Does NOT mark the form dirty.
   */
  ensurePlaceholderRow(): void {
    const controls = this.form.controls.customHeaders.controls;
    const lastControl = controls[controls.length - 1];
    const lastHasKey = lastControl && lastControl.controls['key'].value;
    if (lastHasKey || controls.length === 0) {
      this.form.controls.customHeaders.push(
        new FormGroup({
          key: new FormControl('', { nonNullable: true }),
          value: new FormControl('', { nonNullable: true }),
        }),
        { emitEvent: false }
      );
    }
  }

  removeCustomHeader(index: number): void {
    this.form.controls.customHeaders.removeAt(index);
    this.form.markAsDirty();
    this.ensurePlaceholderRow();
  }

  onTestDelivery(): void {
    const id = this.entityId();
    if (!id || this.isTesting()) return;

    this.isTesting.set(true);
    this.webhookService.testWebhookEndpoint(id).subscribe({
      next: () => {
        this.isTesting.set(false);
        this.toast.success(this.translate.instant('webhooks.test.success'));
      },
      error: err => {
        this.isTesting.set(false);
        const msg = (err?.error as { message?: string })?.message || this.translate.instant('webhooks.test.error');
        this.toast.error(msg);
      },
    });
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  private showSecretDialog(created: WebhookEndpointResponse & { secret: string }): Promise<void> {
    return new Promise<void>(resolve => {
      const dialogRef = this.dialog.open<WebhookSecretDialogResult, WebhookSecretDialogData>(WebhookSecretDialogComponent, {
        width: 'min(96vw, 540px)',
        maxWidth: '96vw',
        maxHeight: '96dvh',
        panelClass: 'ai-chat-dialog-panel',
        backdropClass: 'ai-chat-dialog-backdrop',
        disableClose: true,
        data: { secret: created.secret, endpointName: created.name },
      });

      dialogRef.closed.subscribe(() => resolve());
    });
  }
}
